/**
 * Konjugation front: the whole verb, one field per form.
 *
 * Three decisions are visible in here and each has a reason worth keeping.
 *
 * **The translation is on the front.** This app tests conjugation, and a card
 * that also makes you recall the meaning cannot tell you which of the two you
 * failed. Verbs already tests meaning; this one hands it to you.
 *
 * **You answer until the line is right.** A wrong form is not the end of the
 * question, so the card never advances past a form the user has not produced.
 * But the first miss does NOT reveal the answer — retyping something you have
 * just been shown is copying, not recall — so the first retry is blind and only
 * the second one gets the form as a ghost behind the field.
 *
 * **Only first-try answers count.** Green is right first time, amber is right
 * eventually, and the card's boolean is "all green". Retrying teaches; it does
 * not launder the score, and `memorized` keeps meaning the user produced every
 * form cold.
 */
import { Eye } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { KONJUGATION_LABELS, type KonjugationDetail, type KonjugationPayload } from '@/core/types';
import { cn } from '@/lib/utils';

import type { AnswerEvent, CardFrontProps } from '../types';
import { isFormCorrect } from './answer';
import { fieldsFor } from './fields';

/**
 * Per-row progress. `tries` counts submissions that were wrong; `revealed`
 * means the user asked for the form instead of producing it.
 *
 * The two are kept apart because they are different admissions. Missing `du`
 * twice and never having been able to recall it are different problems, and a
 * single counter cannot tell them apart afterwards.
 */
type RowState = { tries: number; done: boolean; revealed: boolean };

export function CardFront({ card, onAnswer }: CardFrontProps<KonjugationPayload>) {
  const fields = useMemo(() => fieldsFor(card.payload), [card.payload]);

  const [rows, setRows] = useState<RowState[]>(() =>
    fields.map(() => ({ tries: 0, done: false, revealed: false })),
  );
  const [active, setActive] = useState(0);
  const [value, setValue] = useState('');
  const [shake, setShake] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const startRef = useRef<number>(Date.now());
  // The submit handler closes over `rows`; a ref keeps the finish path reading
  // the state it just computed rather than the render's stale copy.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  useEffect(() => {
    startRef.current = Date.now();
    setRows(fields.map(() => ({ tries: 0, done: false, revealed: false })));
    setActive(0);
    setValue('');
  }, [card.id, fields]);

  // Focus follows the active row, and must do it AFTER the commit that moved
  // it. `ref` is attached to whichever input is active, so calling focus()
  // straight after `setActive` focuses the row we are leaving — which is about
  // to be disabled, dropping focus to the body and silently swallowing every
  // keystroke from there on. The card looked finished after one field.
  useEffect(() => {
    inputRef.current?.focus();
  }, [active, card.id]);

  const finish = (finalRows: RowState[]) => {
    // A field the user asked for is as much a miss as one they got wrong: in
    // neither case did they produce the form.
    const missed = fields.filter((_, i) => finalRows[i].tries > 0 || finalRows[i].revealed);
    const producedNothing = finalRows.every((r) => r.revealed);
    const detail: KonjugationDetail = {
      fields: fields.map((f, i) => ({
        key: f.key,
        tries: finalRows[i].tries,
        ...(finalRows[i].revealed ? { revealed: true } : {}),
      })),
    };
    const event: AnswerEvent = {
      // Every field is filled by the time the card ends — what is recorded is
      // whether any of them took more than one honest go.
      correct: missed.length === 0,
      // Read back in the mistakes list, so it names the forms rather than
      // repeating what was typed: "du, ihr" is the useful diagnosis here.
      // `null` when nothing was produced at all, which the shared vocabulary
      // already renders as "skipped".
      user_answer:
        producedNothing || missed.length === 0
          ? null
          : missed.map((f) => KONJUGATION_LABELS[f.key]).join(', '),
      time_ms: Date.now() - startRef.current,
      detail,
    };
    onAnswer(event);
  };

  /** Advance past `from`, finishing the card if it was the last field. */
  const advance = (from: number, next: RowState[]) => {
    setRows(next);
    setValue('');
    if (from + 1 < fields.length) {
      setActive(from + 1);
    } else {
      setActive(fields.length);
      finish(next);
    }
  };

  /**
   * Fill this line in and move on, without asking the user to type it.
   *
   * Deliberately not the same as the two-miss ghost, which shows the form and
   * still makes you produce it. This is for the case where typing it out
   * teaches nothing — you know you do not know — and guessing would only put a
   * fictional answer into the history.
   */
  const revealCurrent = () => {
    if (active >= fields.length) return;
    const next = [...rowsRef.current];
    next[active] = { ...next[active], done: true, revealed: true };
    advance(active, next);
  };

  /** "I don't know" — fill in everything still open and end the card. */
  const revealRest = () => {
    if (active >= fields.length) return;
    const next = rowsRef.current.map((r, i) =>
      i >= active || !r.done ? { ...r, done: true, revealed: true } : r,
    );
    setRows(next);
    setValue('');
    setActive(fields.length);
    finish(next);
  };

  const submit = () => {
    if (active >= fields.length) return;
    const field = fields[active];
    if (!value.trim()) {
      inputRef.current?.focus();
      return;
    }

    if (!isFormCorrect(value, field.answer)) {
      setRows((prev) => {
        const next = [...prev];
        next[active] = { ...next[active], tries: next[active].tries + 1 };
        return next;
      });
      setValue('');
      setShake(true);
      window.setTimeout(() => setShake(false), 320);
      inputRef.current?.focus();
      return;
    }

    const next = [...rowsRef.current];
    next[active] = { ...next[active], done: true };
    advance(active, next);
  };

  const activeRow = rows[active];
  // Blind on the first retry, shown on the second — see the header.
  const showGhost = activeRow ? activeRow.tries >= 2 : false;
  const missedSoFar = rows.filter((r) => r.tries > 0 || r.revealed).length;

  return (
    <div className="border-border bg-card flex flex-col gap-6 rounded-2xl border p-6 shadow-sm sm:p-8">
      <header className="flex flex-col gap-1">
        <span className="text-accent-foreground bg-accent w-fit rounded-full px-2.5 py-0.5 text-xs font-bold tracking-wider uppercase">
          Präsens
        </span>
        <h2 className="font-display mt-1 text-3xl font-semibold sm:text-4xl">{card.de}</h2>
        <p className="text-muted-foreground text-sm">
          {(card.translations.en ?? []).join(', ')}
          {card.translations.pt?.length ? (
            <span className="opacity-70"> · {card.translations.pt.join(', ')}</span>
          ) : null}
        </p>
        {card.payload.separable ? (
          <p className="text-warning text-xs font-semibold tracking-wide uppercase">separable</p>
        ) : null}
      </header>

      <ol className="flex flex-col gap-1">
        {fields.map((field, i) => {
          const row = rows[i];
          const isActive = i === active;
          const wrongNow = isActive && row.tries > 0;
          return (
            <li
              key={field.key}
              className={cn(
                'grid grid-cols-[1.75rem_6.5rem_1fr_1.5rem] items-center gap-x-2 rounded-md py-1 pr-2',
                isActive && 'bg-muted/60',
                row.revealed && 'bg-muted/40',
                row.done && !row.revealed && row.tries === 0 && 'bg-success/10',
                row.done && !row.revealed && row.tries > 0 && 'bg-warning/10',
                // The principal parts are a different question from the six
                // persons, so they get a rule rather than reading as row seven.
                field.group === 'stamm' &&
                  fields[i - 1]?.group !== 'stamm' &&
                  'border-border mt-2 border-t pt-3',
                wrongNow && shake && 'animate-shake',
              )}
            >
              {/* Left of the label, so the eye is never mistaken for the
                  verdict on the right. Only the live row gets one; the empty
                  cell on the others keeps every label on the same axis. */}
              {isActive ? (
                <button
                  type="button"
                  onClick={revealCurrent}
                  title={`Show the ${field.label} form`}
                  aria-label={`Show the answer for ${field.label}`}
                  className="text-muted-foreground hover:text-foreground hover:bg-background focus-visible:ring-ring inline-flex size-6 items-center justify-center rounded focus-visible:ring-2 focus-visible:outline-none"
                >
                  <Eye className="size-3.5" />
                </button>
              ) : (
                <span aria-hidden />
              )}

              <span className="text-muted-foreground text-right font-mono text-sm leading-tight">
                {field.label}
                {field.sub ? (
                  <span className="block text-[0.62rem] tracking-wide opacity-65">{field.sub}</span>
                ) : null}
              </span>

              <span className="relative flex items-center">
                {isActive && showGhost ? (
                  <span
                    aria-hidden
                    className="text-destructive/35 pointer-events-none absolute left-0.5 font-mono text-base"
                  >
                    {field.answer}
                  </span>
                ) : null}
                <input
                  ref={isActive ? inputRef : undefined}
                  value={row.done ? field.answer : isActive ? value : ''}
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      submit();
                    }
                  }}
                  disabled={!isActive}
                  aria-label={field.sub ? `${field.label}, ${field.sub}` : field.label}
                  aria-invalid={wrongNow || undefined}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  className={cn(
                    'focus-visible:border-ring w-full border-0 border-b-2 bg-transparent px-0.5 py-1 font-mono text-base outline-none',
                    'disabled:cursor-default disabled:opacity-100',
                    row.done && row.revealed && 'text-muted-foreground border-transparent',
                    row.done &&
                      !row.revealed &&
                      row.tries === 0 &&
                      'text-success border-transparent',
                    row.done &&
                      !row.revealed &&
                      row.tries > 0 &&
                      'text-foreground border-transparent',
                    !row.done && wrongNow && 'border-destructive text-destructive',
                    !row.done && !wrongNow && 'border-input',
                  )}
                />
              </span>

              <span className="text-center text-sm">
                {row.done && row.revealed ? (
                  <span className="text-muted-foreground" title="shown">
                    –
                  </span>
                ) : null}
                {row.done && !row.revealed && row.tries === 0 ? (
                  <span className="text-success">✓</span>
                ) : null}
                {row.done && !row.revealed && row.tries > 0 ? (
                  <span className="text-warning">▲</span>
                ) : null}
                {wrongNow ? <span className="text-destructive">✗</span> : null}
              </span>
            </li>
          );
        })}
      </ol>

      <p
        aria-live="polite"
        className={cn(
          'min-h-5 text-sm',
          activeRow?.tries === 1 && 'text-destructive',
          showGhost && 'text-warning',
          !activeRow?.tries && 'text-muted-foreground',
        )}
      >
        {showGhost
          ? `It is “${fields[active]?.answer}”. Type it out.`
          : activeRow?.tries === 1
            ? 'Not quite — try again.'
            : `${fields.length} forms. Enter to submit each one.`}
      </p>

      <div className="flex items-center justify-between gap-4">
        <span className="text-muted-foreground text-xs">
          {Math.min(active + 1, fields.length)} of {fields.length}
          {missedSoFar > 0 ? ` · ${missedSoFar} not first try` : ''}
        </span>
        <Button onClick={submit} disabled={active >= fields.length}>
          Check
        </Button>
      </div>

      {/* Where every other app puts it. Fills in whatever is still open rather
          than scoring the card on forms the user never saw. */}
      <button
        type="button"
        onClick={revealRest}
        disabled={active >= fields.length}
        className="text-muted-foreground mx-auto text-sm underline-offset-4 hover:underline disabled:opacity-50"
      >
        I don&apos;t know
      </button>
    </div>
  );
}
